/**
 * "Meine Versand-Standards": per-user defaults for mail and WhatsApp sending (pure helper, no Vue, no API import).
 *
 * Source of truth is the login user (user-login-handler `me/preferences`, field `sendDefaults`), so the defaults follow
 * the user to other devices. localStorage (`vanilla.sendDefaults.v1.<userId>`) is the synchronous cache the dialogs
 * read from, the offline fallback and the source of the one-time migration of defaults saved before the backend
 * supported them (sync state in `vanilla.sendDefaults.v1.<userId>.sync`). The API calls are injected
 * (sendDefaultsSync.helper registers them), so this file stays testable without Vue/axios.
 *
 * Priority in the send dialogs with an occasion ("Versand" block, SendChannelSummary): your default (per occasion, else the
 * general mailbox / number) > the setting of the organisation (Absender & Anlässe) > the only sender you may use.
 * Older dialogs without occasion (SendChoiceSummary / pickers with rememberKey): choice remembered by the dialog > user
 * default > nothing.
 * Every value is a convenience only: the pickers re-validate the referenced ids on every open (still existing, still
 * offered to the user) and silently fall back to an empty field otherwise. The backend checks sender access again.
 */
import {effectiveSignatureMode, isSignatureMode} from '@/helpers/send/signature.helper';

const PREFIX = 'vanilla.sendDefaults.v1';

/** Hex ObjectId */
const ID_PATTERN = /^[a-f0-9]{24}$/i;
/** Template type keys (enum values like `courseCancelledStudent`) */
const TYPE_PATTERN = /^[a-z0-9_]{1,80}$/i;
const MAX_MAIL_LENGTH = 320;
export const MAX_SENDER_NAME_LENGTH = 200;
const MAX_NUMBER_LENGTH = 32;
const NUMBER_PATTERN = /^\+?[0-9 ()/-]{3,}$/;
/** Server limit per template map (user-login-handler) */
const MAX_TEMPLATES = 100;

export type SendChannel = 'mail' | 'whatsApp';

/** Channel modes of an occasion (SendChannelMode values) */
export const OCCASION_CHANNEL_MODES = ['whatsAppElseMail', 'mail', 'whatsApp', 'both'];
/** Occasion keys (SendOccasion values) */
const OCCASION_PATTERN = /^[a-z][a-z0-9]{1,60}$/i;
/** Server limit of the occasion map (user-login-handler) */
const MAX_OCCASIONS = 50;

/**
 * Personal default of ONE send occasion ("Als mein Standard speichern" in the Versand block, "Meine Versand-Standards"
 * › Je Anlass). Only what differs from the organisation; templates of the occasion are stored in the template maps
 * (every occasion has its own template types).
 */
export interface OccasionSendDefault {
  /** SendChannelMode value */
  channelMode?: string;
  /** Mailbox (messenger mailbox address) */
  mailFrom?: string;
  /** Mail api configuration of the mailbox */
  mailConfigId?: string;
  /** WhatsApp api configuration (number) */
  whatsAppConfigId?: string;
  /** "Unterschrift" of the occasion for me (SignatureMode) */
  signature?: string;
}

export interface SendDefaults {
  /** Mail api configuration of the general mailbox (older dialogs: "SMTP-Zugang") */
  mailConfigId?: string;
  /** General mailbox: sender address (smtpUsername or an alias of mailConfigId) */
  mailFrom?: string;
  /** Optional name of the sender shown in the message (placeholder senderName) */
  mailSenderName?: string;
  /** WhatsApp api configuration (phone number) */
  whatsAppConfigId?: string;
  /** Sender number of whatsAppConfigId (kept from the server; the dialogs derive the number from the configuration) */
  whatsAppFrom?: string;
  /** Mail template per CustomerMailTemplateTypes value */
  mailTemplates: Record<string, string>;
  /** WhatsApp template per WhatsAppTemplateTypes value */
  whatsAppTemplates: Record<string, string>;
  /** Personal default per send occasion (SendOccasion value) */
  occasions: Record<string, OccasionSendDefault>;
}

/** What a send dialog currently shows for one channel (input of "Als Standard speichern") */
export interface SendChoice {
  channel: SendChannel;
  configId?: unknown;
  fromMail?: string;
  /** undefined = the dialog has no sender name field (stored value untouched) */
  senderName?: string;
  templateType?: string;
  templateId?: unknown;
}

/** remembered = last choice in this dialog, default = "Meine Versand-Standards", fallback = global setting (e.g. takeover settings) */
export type RememberSource = 'remembered' | 'default' | 'fallback';

export interface RememberCandidate {
  source: RememberSource;
  ids: string[];
}

export function toId(value: unknown): string | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const id = typeof value === 'string' ? value : String((value as { toString(): string }).toString());
  return ID_PATTERN.test(id) ? id : undefined;
}

function toMail(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const mail = value.trim();
  return mail && mail.length <= MAX_MAIL_LENGTH && mail.includes('@') ? mail : undefined;
}

function toSenderName(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const name = value.trim().slice(0, MAX_SENDER_NAME_LENGTH);
  return name || undefined;
}

function toNumber(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const number = value.trim();
  return number && number.length <= MAX_NUMBER_LENGTH && NUMBER_PATTERN.test(number) ? number : undefined;
}

function toTemplateMap(value: unknown): Record<string, string> {
  const result: Record<string, string> = {};
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return result;
  }
  for (const [type, id] of Object.entries(value as Record<string, unknown>).slice(0, MAX_TEMPLATES)) {
    const validId = toId(id);
    if (TYPE_PATTERN.test(type) && validId) {
      result[type] = validId;
    }
  }
  return result;
}

function toOccasionDefault(value: unknown): OccasionSendDefault | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const data = value as Record<string, unknown>;
  const result: OccasionSendDefault = {};
  if (typeof data.channelMode === 'string' && OCCASION_CHANNEL_MODES.includes(data.channelMode)) {
    result.channelMode = data.channelMode;
  }
  const mailFrom = toMail(data.mailFrom);
  if (mailFrom) {
    result.mailFrom = mailFrom.toLowerCase();
    const mailConfigId = toId(data.mailConfigId);
    if (mailConfigId) {
      result.mailConfigId = mailConfigId;
    }
  }
  const whatsAppConfigId = toId(data.whatsAppConfigId);
  if (whatsAppConfigId) {
    result.whatsAppConfigId = whatsAppConfigId;
  }
  if (isSignatureMode(data.signature)) {
    result.signature = data.signature;
  }
  return Object.keys(result).length ? result : undefined;
}

function toOccasionMap(value: unknown): Record<string, OccasionSendDefault> {
  const result: Record<string, OccasionSendDefault> = {};
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return result;
  }
  for (const [occasion, entry] of Object.entries(value as Record<string, unknown>).slice(0, MAX_OCCASIONS)) {
    const valid = OCCASION_PATTERN.test(occasion) ? toOccasionDefault(entry) : undefined;
    if (valid) {
      result[occasion] = valid;
    }
  }
  return result;
}

export function emptySendDefaults(): SendDefaults {
  return {mailTemplates: {}, whatsAppTemplates: {}, occasions: {}};
}

/** Accepts anything (parsed storage content); keeps only well-formed values */
export function normalizeSendDefaults(raw: unknown): SendDefaults {
  const result = emptySendDefaults();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return result;
  }
  const data = raw as Record<string, unknown>;
  const mailConfigId = toId(data.mailConfigId);
  const mailFrom = toMail(data.mailFrom);
  const mailSenderName = toSenderName(data.mailSenderName);
  const whatsAppConfigId = toId(data.whatsAppConfigId);
  const whatsAppFrom = toNumber(data.whatsAppFrom);
  if (mailConfigId) {
    result.mailConfigId = mailConfigId;
    // A sender address only makes sense together with its SMTP access
    if (mailFrom) {
      result.mailFrom = mailFrom;
    }
  }
  if (mailSenderName) {
    result.mailSenderName = mailSenderName;
  }
  if (whatsAppConfigId) {
    result.whatsAppConfigId = whatsAppConfigId;
    if (whatsAppFrom) {
      result.whatsAppFrom = whatsAppFrom;
    }
  }
  result.mailTemplates = toTemplateMap(data.mailTemplates);
  result.whatsAppTemplates = toTemplateMap(data.whatsAppTemplates);
  result.occasions = toOccasionMap(data.occasions);
  return result;
}

export function hasAnySendDefault(defaults: SendDefaults): boolean {
  return !!(defaults.mailConfigId || defaults.mailSenderName || defaults.whatsAppConfigId
    || Object.keys(defaults.mailTemplates).length || Object.keys(defaults.whatsAppTemplates).length
    || Object.keys(defaults.occasions || {}).length);
}

export function buildSendDefaultsKey(userId: unknown): string | undefined {
  const user = toId(userId);
  return user ? `${PREFIX}.${user}` : undefined;
}

function getStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch (e) {
    return null;
  }
}

/** Reads the defaults of the user; unreadable content counts as "no defaults" */
export function readSendDefaults(userId: unknown, storage: Storage | null = getStorage()): SendDefaults {
  const key = buildSendDefaultsKey(userId);
  if (!key || !storage) {
    return emptySendDefaults();
  }
  try {
    const raw = storage.getItem(key);
    return raw ? normalizeSendDefaults(JSON.parse(raw)) : emptySendDefaults();
  } catch (e) {
    return emptySendDefaults();
  }
}

/** Shape of `sendDefaults` in `me/preferences` (user-login-handler whitelist) */
export interface ServerSendDefaults {
  mailConfigId?: string;
  fromMail?: string;
  senderName?: string;
  whatsAppConfigId?: string;
  fromNumber?: string;
  mailTemplates?: Record<string, string>;
  whatsAppTemplates?: Record<string, string>;
  /** Per occasion: channelMode, fromMail (+ mailConfigId), whatsAppConfigId */
  occasions?: Record<string, { channelMode?: string; fromMail?: string; mailConfigId?: string; whatsAppConfigId?: string; signature?: string }>;
}

/** Client defaults -> server payload; null = nothing stored (removes the field at the user) */
export function toServerSendDefaults(defaults: SendDefaults): ServerSendDefaults | null {
  const d = normalizeSendDefaults(defaults);
  if (!hasAnySendDefault(d)) {
    return null;
  }
  const result: ServerSendDefaults = {};
  if (d.mailConfigId) {
    result.mailConfigId = d.mailConfigId;
  }
  if (d.mailFrom) {
    result.fromMail = d.mailFrom;
  }
  if (d.mailSenderName) {
    result.senderName = d.mailSenderName;
  }
  if (d.whatsAppConfigId) {
    result.whatsAppConfigId = d.whatsAppConfigId;
  }
  if (d.whatsAppFrom) {
    result.fromNumber = d.whatsAppFrom;
  }
  if (Object.keys(d.mailTemplates).length) {
    result.mailTemplates = d.mailTemplates;
  }
  if (Object.keys(d.whatsAppTemplates).length) {
    result.whatsAppTemplates = d.whatsAppTemplates;
  }
  if (Object.keys(d.occasions).length) {
    result.occasions = Object.fromEntries(Object.entries(d.occasions).map(([occasion, o]) => [occasion, {
      ...(o.channelMode ? {channelMode: o.channelMode} : {}),
      ...(o.mailFrom ? {fromMail: o.mailFrom} : {}),
      ...(o.mailConfigId ? {mailConfigId: o.mailConfigId} : {}),
      ...(o.whatsAppConfigId ? {whatsAppConfigId: o.whatsAppConfigId} : {}),
      ...(o.signature ? {signature: o.signature} : {}),
    }]));
  }
  return result;
}

/** Server value (anything) -> normalized client defaults */
export function fromServerSendDefaults(raw: unknown): SendDefaults {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return emptySendDefaults();
  }
  const data = raw as Record<string, unknown>;
  return normalizeSendDefaults({
    mailConfigId: data.mailConfigId,
    mailFrom: data.fromMail,
    mailSenderName: data.senderName,
    whatsAppConfigId: data.whatsAppConfigId,
    whatsAppFrom: data.fromNumber,
    mailTemplates: data.mailTemplates,
    whatsAppTemplates: data.whatsAppTemplates,
    occasions: data.occasions && typeof data.occasions === 'object' && !Array.isArray(data.occasions)
      ? Object.fromEntries(Object.entries(data.occasions as Record<string, Record<string, unknown> | null>).map(([occasion, o]) => [occasion, {
        channelMode: o?.channelMode, mailFrom: o?.fromMail, mailConfigId: o?.mailConfigId, whatsAppConfigId: o?.whatsAppConfigId,
        signature: o?.signature,
      }]))
      : undefined,
  });
}

/**
 * Result of the remote save: `saved` at the user, `unsupported` = the backend does not store send defaults yet
 * (old user-login-handler drops the field), `failed` = not reachable / error.
 */
export type RemoteSaveResult = 'saved' | 'unsupported' | 'failed';

/** saved = at the user (all devices), local = only in this browser (old backend), failed = retried on the next load */
export type SendDefaultsSaveResult = 'saved' | 'local' | 'failed';

export interface SendDefaultsRemote {
  save(payload: ServerSendDefaults | null): Promise<RemoteSaveResult>;
}

let registeredRemote: SendDefaultsRemote | null = null;

/** Registered once by sendDefaultsSync.helper (keeps this file free of API imports) */
export function registerSendDefaultsRemote(remote: SendDefaultsRemote | null): void {
  registeredRemote = remote;
}

/** Per user and browser: `migrated` = the server holds the defaults, `dirty` = local change not saved at the server yet */
interface SyncState {
  migrated?: boolean;
  dirty?: boolean;
}

function readSyncState(key: string, storage: Storage | null): SyncState {
  try {
    const raw = storage?.getItem(`${key}.sync`);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? {migrated: parsed.migrated === true, dirty: parsed.dirty === true} : {};
  } catch (e) {
    return {};
  }
}

function writeSyncState(key: string, state: SyncState, storage: Storage | null): void {
  try {
    storage?.setItem(`${key}.sync`, JSON.stringify({migrated: !!state.migrated, dirty: !!state.dirty}));
  } catch (e) {
    // Storage unavailable – the next load syncs again
  }
}

function writeLocal(key: string, defaults: SendDefaults, storage: Storage | null): SendDefaults {
  const normalized = normalizeSendDefaults(defaults);
  try {
    if (!storage) {
      return normalized;
    }
    if (hasAnySendDefault(normalized)) {
      storage.setItem(key, JSON.stringify(normalized));
    } else {
      storage.removeItem(key);
    }
  } catch (e) {
    // Storage full or unavailable – defaults are optional
  }
  return normalized;
}

/** Saves run one after another (the last change wins at the server) */
let queue: Promise<unknown> = Promise.resolve();

function enqueue<T>(task: () => Promise<T>): Promise<T> {
  const run = queue.then(task, task);
  queue = run.catch(() => undefined);
  return run;
}

async function pushRemote(key: string, defaults: SendDefaults, storage: Storage | null, remote: SendDefaultsRemote): Promise<SendDefaultsSaveResult> {
  let result: RemoteSaveResult;
  try {
    result = await remote.save(toServerSendDefaults(defaults));
  } catch (e) {
    result = 'failed';
  }
  const state = readSyncState(key, storage);
  if (result === 'saved') {
    writeSyncState(key, {migrated: true, dirty: false}, storage);
    return 'saved';
  }
  if (result === 'unsupported') {
    // Old backend: the defaults stay in this browser (migrated on a later load once the backend supports them)
    writeSyncState(key, {...state, dirty: false}, storage);
    return 'local';
  }
  return 'failed';
}

/**
 * Stores the defaults (normalized): immediately in the local cache (no defaults at all removes the entry), then at
 * the user via the registered remote. Without remote (tests, no API) only locally.
 */
export function writeSendDefaults(userId: unknown, defaults: SendDefaults, storage: Storage | null = getStorage(),
                                  remote: SendDefaultsRemote | null = registeredRemote): Promise<SendDefaultsSaveResult> {
  const key = buildSendDefaultsKey(userId);
  if (!key) {
    return Promise.resolve('local');
  }
  const normalized = writeLocal(key, defaults, storage);
  if (!remote) {
    return Promise.resolve('local');
  }
  writeSyncState(key, {...readSyncState(key, storage), dirty: true}, storage);
  return enqueue(() => pushRemote(key, normalized, storage, remote));
}

/**
 * Applies the `me/preferences` answer (after login, on opening "Meine Versand-Standards"); `server` null = endpoint not
 * available (offline, old backend without preferences) -> local cache unchanged.
 * - unsaved local change (dirty): pushed to the server (local wins, it is the newer one)
 * - server has defaults: they replace the local cache (other device)
 * - server has none, not migrated yet: local defaults are migrated once (if any)
 * - server has none, migrated before: removed on another device -> local cache cleared
 */
export async function syncSendDefaults(userId: unknown, server: { sendDefaults?: unknown } | null | undefined,
                                       storage: Storage | null = getStorage(),
                                       remote: SendDefaultsRemote | null = registeredRemote): Promise<SendDefaults> {
  const key = buildSendDefaultsKey(userId);
  if (!key) {
    return emptySendDefaults();
  }
  const local = readSendDefaults(userId, storage);
  if (!server) {
    return local;
  }
  const state = readSyncState(key, storage);
  if (state.dirty && remote) {
    await enqueue(() => pushRemote(key, local, storage, remote));
    return local;
  }
  const fromServer = fromServerSendDefaults(server.sendDefaults);
  if (hasAnySendDefault(fromServer)) {
    writeSyncState(key, {migrated: true, dirty: false}, storage);
    return writeLocal(key, fromServer, storage);
  }
  if (!state.migrated) {
    if (hasAnySendDefault(local) && remote) {
      await enqueue(() => pushRemote(key, local, storage, remote));
    }
    return local;
  }
  return writeLocal(key, emptySendDefaults(), storage);
}

/**
 * Default id for the remember context of a picker (see RememberedPickerValue.mixin, `getDefaultContext`):
 * `apiConfig.mail`, `apiConfig.whats_app`, `mailTemplate.<type>`, `waTemplate.<type>`.
 */
export function sendDefaultIdForContext(defaults: SendDefaults, context: string | null | undefined): string | undefined {
  if (!context) {
    return undefined;
  }
  if (context === 'apiConfig.mail') {
    return defaults.mailConfigId;
  }
  if (context === 'apiConfig.whats_app') {
    return defaults.whatsAppConfigId;
  }
  const [kind, type, ...rest] = context.split('.');
  if (!type || rest.length) {
    return undefined;
  }
  if (kind === 'mailTemplate') {
    return defaults.mailTemplates[type];
  }
  if (kind === 'waTemplate') {
    return defaults.whatsAppTemplates[type];
  }
  return undefined;
}

/** Default context of a picker that offers exactly one type; null otherwise (several types: no user default) */
export function defaultContextFor(kind: 'apiConfig' | 'mailTemplate' | 'waTemplate', types: unknown): string | null {
  const list = ([] as unknown[]).concat(types === undefined || types === null ? [] : types)
    .filter((t) => t !== null && t !== undefined && t !== '').map(String);
  if (list.length !== 1 || !TYPE_PATTERN.test(list[0])) {
    return null;
  }
  return `${kind}.${list[0]}`;
}

/**
 * Order in which a picker tries values: remembered choice of the dialog first, then the user default, then a global
 * fallback of the dialog (e.g. the takeover offer settings). Ids already tried are not repeated.
 */
export function rememberCandidates(remembered: string | string[] | undefined, defaultId: unknown, fallbackId?: unknown): RememberCandidate[] {
  const candidates: RememberCandidate[] = [];
  const rememberedIds = ([] as string[]).concat(remembered || []).filter((id) => !!toId(id));
  if (rememberedIds.length) {
    candidates.push({source: 'remembered', ids: rememberedIds});
  }
  const tried = new Set(rememberedIds);
  for (const [source, value] of [['default', defaultId], ['fallback', fallbackId]] as Array<[RememberSource, unknown]>) {
    const id = toId(value);
    if (id && !tried.has(id)) {
      tried.add(id);
      candidates.push({source, ids: [id]});
    }
  }
  return candidates;
}

/** Of the candidate ids, those the picker confirmed (same order); empty = candidate not usable */
export function confirmedIds(candidate: RememberCandidate, verified: unknown[]): string[] {
  const valid = new Set(verified.map((id) => String(id)));
  return candidate.ids.filter((id) => valid.has(id));
}

/**
 * Sender address after choosing / restoring an SMTP access: the user's default address when it belongs to the same
 * access and is still offered, otherwise the first address (previous behaviour).
 */
export function resolveFromMail(options: string[], defaults: SendDefaults, configId: unknown): string | undefined {
  if (!options.length) {
    return undefined;
  }
  const id = toId(configId);
  if (id && defaults.mailConfigId === id && defaults.mailFrom && options.includes(defaults.mailFrom)) {
    return defaults.mailFrom;
  }
  return options[0];
}

/** Sender name on open: what the dialog already has wins, otherwise the user default */
export function resolveSenderName(current: string | null | undefined, defaults: SendDefaults): string {
  const trimmed = (current || '').trim();
  return trimmed ? (current as string) : (defaults.mailSenderName || '');
}

/** Whether the choice holds everything that "Als Standard speichern" needs (something to store) */
export function choiceHasValues(choice: SendChoice): boolean {
  return !!(toId(choice.configId) || (choice.templateType && toId(choice.templateId)));
}

/** "Als Standard speichern": merges the current choice of one channel into the defaults (returns a new object) */
export function applyChoiceToDefaults(defaults: SendDefaults, choice: SendChoice): SendDefaults {
  const next: SendDefaults = {
    ...normalizeSendDefaults(defaults),
  };
  next.mailTemplates = {...next.mailTemplates};
  next.whatsAppTemplates = {...next.whatsAppTemplates};
  const configId = toId(choice.configId);
  const templateId = toId(choice.templateId);
  const templateType = choice.templateType && TYPE_PATTERN.test(choice.templateType) ? choice.templateType : undefined;
  if (choice.channel === 'mail') {
    if (configId) {
      const from = toMail(choice.fromMail);
      if (from) {
        next.mailFrom = from;
      } else if (next.mailConfigId !== configId) {
        // The stored address belongs to the former access (dialogs without sender select keep it for the same access)
        delete next.mailFrom;
      }
      next.mailConfigId = configId;
    }
    if (choice.senderName !== undefined) {
      const name = toSenderName(choice.senderName);
      if (name) {
        next.mailSenderName = name;
      } else {
        delete next.mailSenderName;
      }
    }
    if (templateType && templateId) {
      next.mailTemplates[templateType] = templateId;
    }
  } else {
    if (configId) {
      if (next.whatsAppConfigId !== configId) {
        // The stored number belongs to the former configuration
        delete next.whatsAppFrom;
      }
      next.whatsAppConfigId = configId;
    }
    if (templateType && templateId) {
      next.whatsAppTemplates[templateType] = templateId;
    }
  }
  return normalizeSendDefaults(next);
}

/** Removes ids that were checked and are no longer usable (settings dialog clean-up) */
export function withoutInvalid(defaults: SendDefaults, invalid: { configIds?: string[]; mailTemplateIds?: string[]; whatsAppTemplateIds?: string[] }): SendDefaults {
  const next = normalizeSendDefaults(defaults);
  const configs = new Set(invalid.configIds || []);
  const mailTemplates = new Set(invalid.mailTemplateIds || []);
  const waTemplates = new Set(invalid.whatsAppTemplateIds || []);
  if (next.mailConfigId && configs.has(next.mailConfigId)) {
    delete next.mailConfigId;
    delete next.mailFrom;
  }
  if (next.whatsAppConfigId && configs.has(next.whatsAppConfigId)) {
    delete next.whatsAppConfigId;
    delete next.whatsAppFrom;
  }
  next.mailTemplates = Object.fromEntries(Object.entries(next.mailTemplates).filter(([, id]) => !mailTemplates.has(id)));
  next.whatsAppTemplates = Object.fromEntries(Object.entries(next.whatsAppTemplates).filter(([, id]) => !waTemplates.has(id)));
  return next;
}

/** Summary text of one channel, e.g. "Per E-Mail · info@… über SMTP X · Vorlage Y" */
export function summaryText(parts: { channel: SendChannel; from?: string; senderName?: string; configName?: string; templateName?: string }): string {
  const segments: string[] = [parts.channel === 'mail' ? 'Per E-Mail' : 'Per WhatsApp'];
  const sender = [parts.senderName, parts.from ? (parts.senderName ? `<${parts.from}>` : parts.from) : ''].filter(Boolean).join(' ');
  if (sender && parts.configName) {
    segments.push(`${sender} über ${parts.configName}`);
  } else if (sender || parts.configName) {
    segments.push(sender || parts.configName as string);
  }
  if (parts.templateName) {
    segments.push(`Vorlage ${parts.templateName}`);
  }
  return segments.join(' · ');
}

// ---------------------------------------------------------------- per occasion (Versand block, "Je Anlass")

export interface OccasionTemplateTypes {
  mailType?: string;
  waType: string;
}

/** Personal values of one occasion and where they come from ('occasion' = saved for it, 'general' = general mailbox / number) */
export interface PersonalOccasionValues {
  channelMode?: string;
  mailAddress?: string;
  mailSource?: 'occasion' | 'general';
  whatsAppConfigId?: string;
  whatsAppSource?: 'occasion' | 'general';
  mailTemplate?: string;
  whatsAppTemplate?: string;
  /** "Unterschrift" set by me for this occasion (SignatureMode) */
  signature?: string;
}

/** What the user set for the occasion: own value of the occasion, else the general mailbox / number; templates by type */
export function personalForOccasion(defaults: SendDefaults, occasion: string, types: OccasionTemplateTypes): PersonalOccasionValues {
  const d = normalizeSendDefaults(defaults);
  const own = d.occasions[occasion] || {};
  const result: PersonalOccasionValues = {};
  if (own.channelMode) {
    result.channelMode = own.channelMode;
  }
  if (own.signature) {
    result.signature = own.signature;
  }
  if (own.mailFrom) {
    result.mailAddress = own.mailFrom;
    result.mailSource = 'occasion';
  } else if (d.mailFrom) {
    result.mailAddress = d.mailFrom;
    result.mailSource = 'general';
  }
  if (own.whatsAppConfigId) {
    result.whatsAppConfigId = own.whatsAppConfigId;
    result.whatsAppSource = 'occasion';
  } else if (d.whatsAppConfigId) {
    result.whatsAppConfigId = d.whatsAppConfigId;
    result.whatsAppSource = 'general';
  }
  if (types.mailType && d.mailTemplates[types.mailType]) {
    result.mailTemplate = d.mailTemplates[types.mailType];
  }
  if (d.whatsAppTemplates[types.waType]) {
    result.whatsAppTemplate = d.whatsAppTemplates[types.waType];
  }
  return result;
}

/** Choice of the Versand block / of a row in "Je Anlass" (undefined = not set, i.e. follows the organisation) */
export interface OccasionChoice {
  channelMode?: string | null;
  mailFrom?: string | null;
  mailConfigId?: unknown;
  whatsAppConfigId?: unknown;
  mailTemplate?: unknown;
  whatsAppTemplate?: unknown;
  /** "Unterschrift" (SignatureMode) */
  signature?: string | null;
}

/**
 * Replaces the personal default of the occasion with `choice` (returns a new object). Values equal to `org` (the setting
 * of the organisation) are NOT stored, so the occasion keeps following later changes of the organisation.
 */
export function setOccasionDefault(defaults: SendDefaults, occasion: string, types: OccasionTemplateTypes, choice: OccasionChoice,
                                   org: OccasionChoice = {}): SendDefaults {
  const next = normalizeSendDefaults(defaults);
  next.mailTemplates = {...next.mailTemplates};
  next.whatsAppTemplates = {...next.whatsAppTemplates};
  next.occasions = {...next.occasions};
  const same = (a: unknown, b: unknown) => String(a ?? '').toLowerCase() === String(b ?? '').toLowerCase();
  const entry: OccasionSendDefault = {};
  if (choice.channelMode && !same(choice.channelMode, org.channelMode)) {
    entry.channelMode = choice.channelMode;
  }
  if (choice.mailFrom && !same(choice.mailFrom, org.mailFrom)) {
    entry.mailFrom = choice.mailFrom;
    const configId = toId(choice.mailConfigId);
    if (configId) {
      entry.mailConfigId = configId;
    }
  }
  const waConfig = toId(choice.whatsAppConfigId);
  if (waConfig && !same(waConfig, toId(org.whatsAppConfigId))) {
    entry.whatsAppConfigId = waConfig;
  }
  // equal to the organisation (effective, default team) = follow the organisation
  if (isSignatureMode(choice.signature) && choice.signature !== effectiveSignatureMode(org.signature)) {
    entry.signature = choice.signature;
  }
  const valid = toOccasionDefault(entry);
  if (valid) {
    next.occasions[occasion] = valid;
  } else {
    delete next.occasions[occasion];
  }
  const mailTemplate = toId(choice.mailTemplate);
  if (types.mailType) {
    if (mailTemplate && !same(mailTemplate, toId(org.mailTemplate))) {
      next.mailTemplates[types.mailType] = mailTemplate;
    } else {
      delete next.mailTemplates[types.mailType];
    }
  }
  const waTemplate = toId(choice.whatsAppTemplate);
  if (waTemplate && !same(waTemplate, toId(org.whatsAppTemplate))) {
    next.whatsAppTemplates[types.waType] = waTemplate;
  } else {
    delete next.whatsAppTemplates[types.waType];
  }
  return normalizeSendDefaults(next);
}

/** "Zurücksetzen" of one occasion: own values and its templates removed (the general mailbox / number stay) */
export function resetOccasionDefault(defaults: SendDefaults, occasion: string, types: OccasionTemplateTypes): SendDefaults {
  const next = normalizeSendDefaults(defaults);
  next.occasions = {...next.occasions};
  delete next.occasions[occasion];
  next.mailTemplates = {...next.mailTemplates};
  next.whatsAppTemplates = {...next.whatsAppTemplates};
  if (types.mailType) {
    delete next.mailTemplates[types.mailType];
  }
  delete next.whatsAppTemplates[types.waType];
  return normalizeSendDefaults(next);
}

/** Mailbox (messenger) a sender option offers */
export interface MailboxOptionLike {
  mailAddress: string;
  apiConfigurationId: unknown;
}

/**
 * Old general mail default (SMTP access + address) -> mailbox: the mailbox with that address, else the only mailbox of
 * that access; 'invalid' = a value was stored but no mailbox of the user matches (shown as "nicht mehr gültig").
 */
export function mailboxOfDefaults(defaults: SendDefaults, options: MailboxOptionLike[]): { mailbox?: MailboxOptionLike; state: 'none' | 'ok' | 'mapped' | 'invalid' } {
  const d = normalizeSendDefaults(defaults);
  if (!d.mailConfigId && !d.mailFrom) {
    return {state: 'none'};
  }
  const from = (d.mailFrom || '').toLowerCase();
  const byAddress = from ? options.find((o) => o.mailAddress.toLowerCase() === from) : undefined;
  if (byAddress) {
    return {mailbox: byAddress, state: 'ok'};
  }
  const ofAccess = d.mailConfigId ? options.filter((o) => toId(o.apiConfigurationId) === d.mailConfigId) : [];
  if (ofAccess.length === 1) {
    return {mailbox: ofAccess[0], state: 'mapped'};
  }
  return {state: 'invalid'};
}
